import React, {
    forwardRef,
    useState,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import debounce from 'lodash/debounce';

import { ReactComponent as Arrow } from 'assets/icons/arrow_back.svg';

import { capitalize } from 'shared/string';

import Users from './components/Users/Users';
import Videos from './components/Videos/Videos';
import Lessons from './components/Lessons/Lessons';
import Articles from './components/Articles/Articles';
import Masterclasses from './components/Masterclasses/Masterclasses';

import Spinner from 'UI/Spinner/Spinner';

import STRAPI from 'API_STRAPI';

import styles from './Search.module.scss';

const BASE_LIMIT = 4;
const LIMIT_PER_CATEGORY = 12;

const Search = forwardRef(
    ({ open, showRegisterModal, toggleSearch, user }, ref) => {
        const [data, setData] = useState([]);
        const [loading, setLoading] = useState(false);
        const [inputValue, setInputValue] = useState('');
        const [totalNumber, setTotalNumber] = useState(null);
        const [categories] = useState([
            'all',
            'lessons',
            'videos',
            'users',
            'articles',
            'masterclasses',
        ]);
        const [selectedCategory, setSelectedCategory] = useState('all');
        const [content, setContent] = useState(false);
        const [offset, setOffset] = useState(0);
        const [limit, setLimit] = useState(BASE_LIMIT);
        const [noMoreData, setNoMoreData] = useState(false);
        const closeRef = useRef();

        const selectCategory = (type) => {
            if (type !== 'all') {
                setLimit(LIMIT_PER_CATEGORY);
            } else {
                setLimit(BASE_LIMIT);
            }

            if (type !== selectedCategory) {
                setSelectedCategory(type);
                setNoMoreData(false);
                setOffset(0);
                setData([]);
            }
        };

        const loadMoreData = () => {
            setOffset(offset + limit);
        };

        const fetchData = async (qs) => {
            if (qs) {
                setLoading(true);

                try {
                    const response = await STRAPI.get(`/search/${qs &&
                        qs.trim()}?
					${limit ? `limit=${limit}` : ''}
					${offset ? `&offset=${offset}` : ''}
					${selectedCategory !== 'all' ? `&target_type=${selectedCategory}` : ''}`);
                    if (selectedCategory !== 'all') {
                        const formatedData =
                            data && data[0]
                                ? [
                                      {
                                          ...data[0],
                                          items: [
                                              ...data[0].items,
                                              ...response.data.results[0].items,
                                          ],
                                      },
                                  ]
                                : response.data.results;
                        setData([...formatedData]);
                    } else {
                        setData([...data, ...response.data.results]);
                        setTotalNumber(response.data.total_number_results);
                    }
                    setContent(true);
                    setLoading(false);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.warn(error);
                    setLoading(false);
                }
            }
        };

        const onChange = useCallback(
            debounce((e) => onSearchChange(e), 300),
            [selectedCategory]
        );

        const onSearchChange = (e) => {
            fetchData(e.target.value);
        };

        const inputChange = (e) => {
            e.persist();
            setInputValue(e.target.value);
            if (e.target.value) {
                onChange(e);
            } else {
                setData([]);
                setContent(false);
            }
        };

        useEffect(() => {
            if (content && open) {
                document.body.style.overflow = 'hidden';
            }

            if (!open) {
                setData([]);
                setInputValue('');
                setContent(false);
                setSelectedCategory('all');
                setNoMoreData(false);
                setOffset(0);
            }

            return () => (document.body.style.overflow = 'unset');
        }, [content, open]);

        useEffect(() => {
            fetchData(inputValue);
        }, [offset, selectedCategory]);

        const useClickOutside = (ref, handler) => {
            useEffect(() => {
                const listener = (e) => {
                    if (
                        !ref.current ||
                        e.target.id === 'open' ||
                        !open ||
                        ref.current.contains(e.target)
                    ) {
                        return;
                    }

                    handler(e);
                };

                document.addEventListener('click', listener);

                return () => {
                    document.removeEventListener('click', listener);
                };
            }, [ref, handler]);
        };

        useClickOutside(closeRef, toggleSearch);

        const renderResults = (item) => {
            switch (item.target_type) {
                case 'articles':
                    return item.items.length !== 0 ? (
                        <Articles
                            data={item}
                            key={item.id}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={selectCategory}
                            loadMoreData={loadMoreData}
                            noMoreData={noMoreData}
                            limit={limit}
                            showRegisterModal={showRegisterModal}
                        />
                    ) : item.item_count === 0 &&
                      item.target_type === selectedCategory ? (
                        <NoData />
                    ) : null;
                case 'users':
                    return item.items.length !== 0 ? (
                        <Users
                            data={item}
                            key={item.id}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={selectCategory}
                            loadMoreData={loadMoreData}
                            noMoreData={noMoreData}
                            limit={limit}
                        />
                    ) : item.item_count === 0 &&
                      item.target_type === selectedCategory ? (
                        <NoData />
                    ) : null;
                case 'videos':
                    return item.items.length !== 0 ? (
                        <Videos
                            data={item}
                            key={item.id}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={selectCategory}
                            loadMoreData={loadMoreData}
                            noMoreData={noMoreData}
                            limit={limit}
                            showRegisterModal={showRegisterModal}
                        />
                    ) : item.item_count === 0 &&
                      item.target_type === selectedCategory ? (
                        <NoData />
                    ) : null;
                case 'masterclasses':
                    return item.items.length !== 0 ? (
                        <Masterclasses
                            search="masterclass"
                            user={user}
                            data={item}
                            key={item.id}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={selectCategory}
                            loadMoreData={loadMoreData}
                            noMoreData={noMoreData}
                            limit={limit}
                            showRegisterModal={showRegisterModal}
                        />
                    ) : item.item_count === 0 &&
                      item.target_type === selectedCategory ? (
                        <NoData />
                    ) : null;
                case 'lessons':
                    return item.items.length !== 0 ? (
                        <Lessons
                            search="lesson"
                            data={item}
                            key={item.id}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={selectCategory}
                            loadMoreData={loadMoreData}
                            noMoreData={noMoreData}
                            limit={limit}
                            showRegisterModal={showRegisterModal}
                        />
                    ) : item.item_count === 0 &&
                      item.target_type === selectedCategory ? (
                        <NoData />
                    ) : null;
                default:
                    return null;
            }
        };

        return (
            <div className={styles.search_wrapper} ref={closeRef}>
                <div
                    className={[
                        styles.search,
                        open ? styles.open : styles.close,
                    ].join(' ')}
                >
                    <div className={styles.search_content}>
                        <div className={styles.search_box}>
                            <input
                                className={styles.input}
                                onChange={inputChange}
                                value={!open ? '' : inputValue}
                                // autoFocus
                                ref={ref}
                                noValidate
                                type="text"
                                placeholder="What are you looking for ?"
                            />
                            {loading ? (
                                <Spinner
                                    size={42}
                                    style={{
                                        position: 'absolute',
                                        right: '-2px',
                                    }}
                                />
                            ) : (
                                <Arrow />
                            )}
                        </div>
                        <div
                            className={[
                                styles.categories,
                                content ? styles.show : styles.hide,
                            ].join(' ')}
                        >
                            <div
                                className={
                                    totalNumber === 0 ? styles.no_results : null
                                }
                            >
                                {`${totalNumber} ${
                                    totalNumber !== 1 ? 'results' : 'result'
                                } found${totalNumber === 0 ? '' : ':'}`}
                            </div>
                            {totalNumber !== 0 &&
                                categories.map((category) => {
                                    return (
                                        <span
                                            key={category}
                                            className={
                                                category === selectedCategory
                                                    ? styles.active
                                                    : null
                                            }
                                            onClick={() =>
                                                selectCategory(category)
                                            }
                                        >
                                            {category !== 'masterclasses'
                                                ? capitalize(category)
                                                : 'Masterclass episodes'}
                                        </span>
                                    );
                                })}
                        </div>
                    </div>
                </div>
                <div
                    className={[
                        styles.results_wrapper,
                        content && open ? styles.open : styles.close,
                    ].join(' ')}
                    ref={closeRef}
                >
                    {data && totalNumber !== 0 ? (
                        data.map((item) => {
                            return renderResults(item);
                        })
                    ) : (
                        <NoData />
                    )}
                </div>
            </div>
        );
    }
);

const NoData = () => {
    return (
        <div className={styles.no_data}>
            We're sorry, no results were found for your search
        </div>
    );
};

export default Search;
